<template>
  <div v-if="offer" class="dj-variant-container flex flex-wrap items-start sm:flex-nowrap">
    <div class="dj-variant-main">
      <div class="dj-variant-info">
        <div>
          <span class="dj-variant-externalId">{{ variant.externalId }}</span>
          <span class="dj-variant-mpn">/ {{ variant.mpn }}</span>
        </div>
        <div class="dj-variant-units">
          {{ $t('DjProductSearchResultVariant.variantUnit', nbByBox, { nbByBox }) }}
        </div>
      </div>
      <div class="dj-variant-actions">
        <DjAddToCartButton
          :cart-id="cartId"
          type-id="offerPriceId"
          :lines="[
            {
              offerPriceId,
              quantity: 1,
              updateAction: 'ADD_QUANTITY',
            },
          ]"
          :is-authenticated="isAuthenticated"
          @added="handleProductAdded"
          @error="handleAddToCartError"
        />
        <DjAddToQuoteButton
          :lines="[
            {
              variantId,
              quantity: 1,
            },
          ]"
          :is-authenticated="isAuthenticated"
        />
        <DjAddToBuyingListButton
          :is-authenticated="isAuthenticated"
          type-id="offerPriceId"
          :lines="[
            {
              offerPriceId,
              quantity: 1,
              updateAction: 'ADD_QUANTITY',
            },
          ]"
        />
      </div>
    </div>
    <div class="dj-variant-price">
      <div class="dj-price">
        <span class="dj-main-price mr-1">{{ formattedPrixutv }}</span>
        <UTooltip :text="utv" :popper="{ placement: 'top' }">
          <h3 class="unite ellipsis">
            {{ $t('DjProductSearchResultVariant.withoutTax') }} {{ utv }}
          </h3>
        </UTooltip>
      </div>
      <div class="dj-price">
        <span class="dj-secondary-price mr-1">{{ formattedProductUnitPrice }}</span>
        <div class="unite ellipsis">
          {{ $t('DjProductSearchResultVariant.unitWithoutTax') }}
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import type { ProductVariantResponse } from '@djust-b2b/djust-front-sdk/lib/interfaces/models/product';

  const props = defineProps<{
    variant: ProductVariantResponse;
  }>();

  const cartStore = useCartStore();
  const useStore = useUserStore();
  const isAuthenticated = computed(() => useStore.isAuthenticated);
  const cartId = computed(() => cartStore.cart.id);
  const { getProductVariantOffers } = useProduct();

  const { getUserCurrency } = useUser();
  const currency = computed(() => getUserCurrency());
  const offer = ref();

  onMounted(async () => {
    const { success, offers } = (await getProductVariantOffers({
      productIdentifier: props.variant.externalId,
      productIdType: 'EXTERNAL_ID',
      currency,
    })) as GetProductVariantOffersResponse;

    if (success) {
      offer.value = offers[0];
    }
  });

  const nbByBox = computed(() => {
    const attribute = props.variant.attributeValues.find(
      (a) => a.attribute.externalId === 'nb_pce_colis'
    );
    return attribute?.value;
  });

  const offerPriceId = computed(() => offer.value?.offerPrices[0].externalId);
  const variantId = computed(() => offer.value?.offerPrices[0].externalId);

  const handleProductAdded = (response: any) => {
    console.log('Product added successfully:', response);
  };

  const handleAddToCartError = (error: any) => {
    console.error('Error adding product to cart:', error);
  };

  const getOfferCfValue = (offer: any, id: string) => {
    const cf = offer.offerInventory.customFieldValues
      .map((cf: any) => cf.value)
      .find((cf: any) => cf.customField.externalId === id);
    return cf?.value;
  };

  const { getOfferPriceUnitPrice } = useOffer();
  const { getFormattedPrice } = useUtils();
  //Computed product unit price
  const productUnitPrice = computed(() =>
    offer.value ? getOfferPriceUnitPrice(offer.value.offerPrices[0]) : null
  );
  const formattedProductUnitPrice = computed(() => {
    const price = Number(productUnitPrice.value);
    return price !== null ? getFormattedPrice(price, currency.value) : '';
  });

  // Computed CF offer
  const prixutv = computed(() => (offer.value ? getOfferCfValue(offer.value, 'prix_UTV') : null));
  const formattedPrixutv = computed(() => {
    const price = Number(prixutv.value);
    return price !== null ? getFormattedPrice(price, currency.value) : '';
  });
  const utv = computed(() => (offer.value ? getOfferCfValue(offer.value, 'UTV') : null));
</script>

<style lang="scss" scoped>
  @use './DjProductSearchResultVariant.scss';
</style>
